import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock, Ban, MessageCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Avatar from "../components/social/Avatar";
import FriendButton from "../components/social/FriendButton";
import PostCard from "../components/social/PostCard";
import { themeGradient } from "../lib/themes";
import { Button } from "../components/ui/button";

export default function UserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/social/users/${id}`);
      setProfile(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const like = async (post) => {
    const { data } = await api.post(`/posts/${post.post_id}/like`);
    setProfile((pf) => ({ ...pf, posts: pf.posts.map((p) => p.post_id === post.post_id ? { ...p, likes: data.likes } : p) }));
  };
  const comment = async (post, txt) => {
    const { data } = await api.post(`/posts/${post.post_id}/comment`, { text: txt });
    setProfile((pf) => ({ ...pf, posts: pf.posts.map((p) => p.post_id === post.post_id ? { ...p, comments: [...(p.comments || []), data] } : p) }));
  };
  const react = async (post, sticker) => {
    const { data } = await api.post(`/posts/${post.post_id}/react`, { sticker });
    setProfile((pf) => ({ ...pf, posts: pf.posts.map((p) => p.post_id === post.post_id ? { ...p, reactions: data.reactions } : p) }));
  };

  const block = async () => {
    try {
      await api.post(`/social/block/${id}`);
      toast.success("Person blocked");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const messageFriend = async () => {
    try {
      const { data } = await api.post("/threads/direct", { user_id: id });
      navigate(`/messages?t=${data.thread_id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not open chat"); }
  };

  const isSelf = profile && user && profile.user_id === user.user_id;

  return (
    <div className="min-h-screen bg-[#FFFDF7] pb-16">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate("/social")} data-testid="back-to-social-btn"
          className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-800 font-semibold text-sm mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to feed
        </button>

        {loading && <p className="text-center text-slate-400 py-12">Loading profile…</p>}

        {!loading && !profile && <p className="text-center text-slate-400 py-12">This person could not be found.</p>}

        {!loading && profile && (
          <>
            <div className="bg-white rounded-3xl border border-amber-100/60 shadow-lg overflow-hidden" data-testid="user-profile-header">
              <div className="h-32 sm:h-40"
                style={profile.cover ? { backgroundImage: `url(${profile.cover})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: themeGradient(profile.theme) }} />
              <div className="px-6 pb-6 -mt-12">
                <Avatar user={profile} size={96} className="ring-4 ring-white" testid="user-profile-avatar" />
                <div className="mt-3 flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <h1 className="font-display font-bold text-2xl text-slate-900" data-testid="user-profile-name">{profile.name}</h1>
                    {profile.bio && <p className="text-slate-500 mt-1">{profile.bio}</p>}
                    <div className="flex gap-4 mt-3 text-sm">
                      <span className="text-slate-600"><b className="text-slate-900">{profile.friends_count}</b> friends</span>
                      <span className="text-slate-600"><b className="text-slate-900">{profile.posts_count}</b> posts</span>
                    </div>
                  </div>
                  {!isSelf && (
                    <div className="flex items-center gap-2">
                      {profile.status === "friends" && (
                        <Button onClick={messageFriend} data-testid="message-user-btn"
                          className="rounded-full h-9 bg-[#9B5DE5] hover:bg-[#843fd6] font-bold">
                          <MessageCircle className="h-4 w-4 mr-1.5" /> Message
                        </Button>
                      )}
                      <FriendButton userId={profile.user_id} status={profile.status} onChanged={load} />
                      {profile.status !== "blocked" && profile.status !== "blocked_by" && (
                        <Button variant="outline" size="icon" onClick={block} title="Block" data-testid="block-user-btn"
                          className="rounded-full h-9 w-9 text-slate-400 hover:text-red-500">
                          <Ban className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-6" data-testid="user-profile-posts">
              {!profile.can_see_posts ? (
                <div className="bg-white rounded-3xl border border-amber-100/60 shadow-lg p-10 text-center">
                  <Lock className="h-8 w-8 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 font-semibold">This profile is private.</p>
                  <p className="text-slate-400 text-sm mt-1">Become friends to see their posts.</p>
                </div>
              ) : profile.posts.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No posts yet.</p>
              ) : (
                profile.posts.map((p) => <PostCard key={p.post_id} post={p} user={user} onLike={like} onComment={comment} onReact={react} />)
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
